// Composer Studio — 캔버스 상대좌표 환산 유틸 (오버레이 공용)
//
// 편집 캔버스 위에 절대배치되는 오버레이(선택/드래그/리사이즈/패딩/바인딩 배지/인라인 에디터)는
// 모두 엔진 DOM의 [data-composer-node-id] 요소 rect를 "캔버스 기준 상대좌표"로 환산해 위치를 잡는다.
// 그 환산식(getBoundingClientRect 차분)과 attribute selector 이스케이프가 파일마다 복제돼 있어
// 한 곳으로 모은다. 좌표 계산은 기존과 동일(r.top - c.top 등) — 순수 추출.

/** 캔버스 기준 상대 rect */
export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** CSS attribute selector value 이스케이프(따옴표/백슬래시). */
export function cssEscape(v: string): string {
  return v.replace(/["\\]/g, '\\$&');
}

/**
 * 오버레이 React key 유일화 팩토리. 반복영역 전개 복제 노드/셀은 같은 정체성 id/좌표를 여러 DOM
 * 요소가 공유하므로(updateCell 매칭용 설계) DOM 등장 순번을 접미(#2, #3…)해 key 중복을 막는다.
 * 호출마다 독립 카운터(Map)를 갖는 클로저를 반환한다 — PaddingOverlay/BindingBadgeOverlay 공유.
 */
export function makeUniqKey(): (base: string) => string {
  const seen = new Map<string, number>();
  return (base: string): string => {
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n > 1 ? `${base}#${n}` : base;
  };
}

/**
 * 캔버스의 줌(transform scale) 배율 — 시각폭(getBoundingClientRect) ÷ 레이아웃폭(offsetWidth)로 유도.
 * 줌 래퍼 밖이거나 측정 불가면 1.
 */
export function canvasScale(canvas: HTMLElement): number {
  const c = canvas.getBoundingClientRect();
  return canvas.offsetWidth > 0 ? c.width / canvas.offsetWidth : 1;
}

/**
 * el의 canvas 기준 상대 rect — **줌(transform scale) 역보정판**. 작성 캔버스(writeCanvasEl)는 줌
 * 래퍼(예: 81%) **안**에 있어서 getBoundingClientRect가 시각(스케일된) px를 주는데, 오버레이도
 * 같은 스케일 공간 안에 absolute로 놓이므로 CSS top/left는 **콘텐츠(미스케일) px**여야 한다.
 * 시각 px를 그대로 쓰면 스케일이 이중 적용돼 오버레이가 오프셋×(1-scale)만큼 위/왼쪽으로 밀린다
 * (2026-07-20 제보: 81% 줌에서 분기 탭이 다른 섹션 위에 뜸).
 *
 * 미스케일 px는 getComputedStyle(grid track)·offsetWidth 같은 **레이아웃 값과 같은 단위**라,
 * 경계 드래그(WidgetEdgeResizeOverlay)의 폭 계산도 이 rect를 기준으로 한다.
 */
export function rectRelativeToUnscaled(canvas: HTMLElement, el: HTMLElement): Rect {
  const c = canvas.getBoundingClientRect();
  const scale = canvasScale(canvas);
  const r = el.getBoundingClientRect();
  return {
    top: (r.top - c.top) / scale,
    left: (r.left - c.left) / scale,
    width: r.width / scale,
    height: r.height / scale,
  };
}

/** el의 canvas 기준 상대 rect. */
export function rectRelativeTo(canvas: HTMLElement, el: HTMLElement): Rect {
  const c = canvas.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return { top: r.top - c.top, left: r.left - c.left, width: r.width, height: r.height };
}

/** data-composer-node-id 요소의 canvas 상대 rect (없으면 null). */
export function getNodeRect(canvas: HTMLElement, nodeId: string): Rect | null {
  const el = canvas.querySelector<HTMLElement>(`[data-composer-node-id="${cssEscape(nodeId)}"]`);
  return el ? rectRelativeTo(canvas, el) : null;
}

/**
 * data-composer-node-id 요소들의 canvas 상대 rect 배열. 표가 페이지 분할(PagedEditBody rowRanges)로
 * 같은 node-id 조각 여러 개로 렌더될 수 있으므로 전부 반환한다(선택 오버레이가 조각마다 outline).
 */
export function getNodeRects(canvas: HTMLElement, nodeId: string): Rect[] {
  const els = canvas.querySelectorAll<HTMLElement>(`[data-composer-node-id="${cssEscape(nodeId)}"]`);
  return Array.from(els, (el) => rectRelativeTo(canvas, el));
}

/** data-composer-cell="tableId::row::col" 요소의 canvas 상대 rect (없으면 null). */
export function getCellRect(
  canvas: HTMLElement,
  tableId: string,
  row: number,
  col: number,
): Rect | null {
  const sel = `[data-composer-cell="${cssEscape(`${tableId}::${row}::${col}`)}"]`;
  const el = canvas.querySelector<HTMLElement>(sel);
  return el ? rectRelativeTo(canvas, el) : null;
}
